import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export interface Company {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  description?: string;
  registered_date?: any;
  created_at?: any;
  updated_at?: any;
  active?: boolean;
  ignore?: boolean;
  logo?: string;
}

/**
 * Get all companies in the database (no restrictions)
 * @returns Array of Company objects
 */
export async function getAllCompanies(): Promise<Company[]> {
  const companiesRef = collection(db, 'companies');
  const snapshot = await getDocs(companiesRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Company[];
}

/**
 * Get all user-company relationships for a user, joined with company details.
 * @param userId The user's UID
 * @returns Array of user-company objects with company details
 */
export async function getUserCompaniesWithDetails(userId: string) {
  const userCompaniesRef = collection(db, 'userCompanies');
  const q = query(
    userCompaniesRef,
    where('userId', '==', userId),
    where('active', '==', true)
  );
  const userCompaniesSnap = await getDocs(q);
  if (userCompaniesSnap.empty) return [];

  const userCompaniesData = userCompaniesSnap.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      companyId: data.companyId,
      role: data.role,
      permissions: data.permissions || [],
      active: data.active,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by
    };
  });

  const companyIds = [...new Set(userCompaniesData.map(uc => uc.companyId))];
  if (companyIds.length === 0) return [];

  const companiesRef = collection(db, 'companies');
  const companiesQuery = query(companiesRef, where('__name__', 'in', companyIds));
  const companiesSnap = await getDocs(companiesQuery);
  const companiesMap = new Map();
  companiesSnap.docs.forEach(doc => {
    companiesMap.set(doc.id, { id: doc.id, ...doc.data() });
  });

  return userCompaniesData.map(userCompany => {
    const company = companiesMap.get(userCompany.companyId);
    return {
      // User-company relationship data
      id: userCompany.id,
      userId: userCompany.userId,
      companyId: userCompany.companyId,
      role: userCompany.role,
      permissions: userCompany.permissions || [],
      active: userCompany.active,
      created_at: userCompany.created_at,
      updated_at: userCompany.updated_at,
      created_by: userCompany.created_by,
      // Company details
      companyName: company?.name || 'Unknown Company',
      companyDescription: company?.description || null,
      companyActive: company?.active || false,
      companyCreatedAt: company?.created_at || null,
      companyLogo: company?.logo || null,
      companyConfig: company?.config || null
    };
  });
}

/**
 * For superadmin: Get all user-company relationships for a user profile, joined with company details.
 * @param profile The user profile object (with uid, email, etc.)
 * @returns Array of user-company objects with company details
 */
export async function getUserCompaniesForSuperAdmin(profile: any) {
  const companiesRef = collection(db, 'companies');
  const snapshot = await getDocs(companiesRef);
  return snapshot.docs
    .map(doc => {
      const company = doc.data();
      return {
        id: doc.id,
        userId: profile.uid,
        role: profile.globalRole,
        active: profile.active,
        created_at: profile.created_at,
        updated_at: profile.updated_at,
        created_by: profile.created_by,
        companyId: doc.id,
        companyName: company?.name || 'Unknown Company',
        companyDescription: company?.description || null,
        companyActive: company?.active || false,
        companyCreatedAt: company?.created_at || null,
        companyLogo: company?.logo || null,
        companyConfig: company?.config || null
      };
    })
    .sort((a, b) => a.companyName.localeCompare(b.companyName));
}
